import * as dateFns from 'date-fns';
import * as dateFnsTz from 'date-fns-tz';

/** @param {string} timeStr */
function splitTime(timeStr) {
  const [hour, min] = timeStr.split(':');
  return [parseInt(hour, 10), parseInt(min, 10)];
}

/**
 * @param {number} hours
 * @param {number} minutes
 */
function formatShortHoursMins(hours, minutes) {
  const adjustedDate = dateFns.set(new Date(), {
    hours,
    minutes,
  });
  return dateFns.format(adjustedDate, minutes === 0 ? 'haaa' : 'h:mmaaa');
}

/**
 * @param {FF.FormattedWorkHours|null} workHours
 * @param {string} timeZone
 * */
export function getOpenStatus(workHours, timeZone) {
  if (!workHours) {
    return null;
  }

  // use location time zone to offset compare date
  const timeZoneAdjustedDate = dateFnsTz.formatInTimeZone(
    new Date(),
    timeZone,
    'yyyy-MM-dd HH:mm:ss',
  );
  const currentDate = dateFnsTz.toDate(
    timeZoneAdjustedDate.split(' ').join('T'),
  );

  // matches days as represented by date-fns
  /** @type {Array<FF.DaysOfWeek>} DAY_INDEX */
  const DAY_INDEX = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const currentDay = currentDate.getDay();
  const currentDayWorkingHours = workHours[DAY_INDEX[currentDay]];

  if (currentDayWorkingHours) {
    for (let i = 0; i < currentDayWorkingHours.length; i += 1) {
      const [startTime, endTime] = currentDayWorkingHours[i];

      const [startTimeHour, startTimeMin] = splitTime(startTime);
      const [endTimeHour, endTimeMin] = splitTime(endTime);

      const startDateTime = dateFns.set(currentDate, {
        hours: startTimeHour,
        minutes: startTimeMin,
      });

      const endDateTime = dateFns.set(currentDate, {
        hours: endTimeHour,
        minutes: endTimeMin,
      });

      const isCurrentDay24Hours = startTime === '00:00' && endTime === '23:59';

      const opensTodayButNotYet = dateFns.isBefore(currentDate, startDateTime);
      const currentlyOpenToday = dateFns.isWithinInterval(currentDate, {
        start: startDateTime,
        end: endDateTime,
      });

      if (isCurrentDay24Hours || (currentlyOpenToday && endTime === '23:59')) {
        for (let j = 1; j < 7; j += 1) {
          const nextDay = DAY_INDEX[(currentDay + j) % 7];
          const nextDayWorkingHours = workHours[nextDay];
          if (!nextDayWorkingHours) {
            return { isOpenNow: true, message: `Open til ${nextDay} 12am` };
          }
          if (nextDayWorkingHours[0][0] !== '00:00') {
            return { isOpenNow: true, message: `Open til ${nextDay} 12am` };
          }
          if (nextDayWorkingHours[0][1] !== '23:59') {
            const [nextDayEndTimeHour, nextDayEndTimeMin] = splitTime(
              nextDayWorkingHours[0][1],
            );

            return {
              isOpenNow: true,
              message: `Open til ${nextDay} ${formatShortHoursMins(
                nextDayEndTimeHour,
                nextDayEndTimeMin,
              )}`,
            };
          }
        }
        return { isOpenNow: true, message: `Open 24 hrs` };
      }

      if (opensTodayButNotYet) {
        return {
          isOpenNow: false,
          message: `Opens at ${formatShortHoursMins(
            startTimeHour,
            startTimeMin,
          )}`,
        };
      }

      if (currentlyOpenToday) {
        return {
          isOpenNow: true,
          message: `Open til ${formatShortHoursMins(endTimeHour, endTimeMin)}`,
        };
      }
    }
  }

  // find day/time when its next open
  for (let i = 1; i < 7; i += 1) {
    const nextDay = DAY_INDEX[(currentDay + i) % 7];
    const nextDayWorkingHours = workHours[nextDay];
    if (nextDayWorkingHours) {
      const [startTimeHour, startTimeMin] = splitTime(
        nextDayWorkingHours[0][0],
      );
      return {
        isOpenNow: false,
        message: `Opens on ${nextDay} ${formatShortHoursMins(
          startTimeHour,
          startTimeMin,
        )}`,
      };
    }
  }

  return null;
}

/**
 * @param {FF.FormattedWorkHours} workHours
 * @param {boolean=} useFullDaysName
 */
export const formatDates = (workHours, useFullDaysName = false) => {
  /** @param {string} str */
  const formatTimeString = str => {
    let hour = parseInt(str.slice(0, 2), 10);
    const minute = parseInt(str.slice(3), 10);
    let suffix = 'AM';

    if (hour > 12) {
      suffix = 'PM';
      hour -= 12;
    }
    if (hour === 0) hour = 12;

    return `${hour}${minute ? `:${minute}` : ''}${suffix}`;
  };

  /** @type {Array<FF.DaysOfWeek>} DAY_INDEX */
  const DAY_INDEX = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const FULL_DAY_INDEX = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  // Make formatted time strings for each day of the week
  const dayRanges = DAY_INDEX.map(day => {
    const ranges = workHours[day];
    if (!ranges || !ranges[0][0]) return 'CLOSED';
    const formattedRanges = ranges.map(([startStr, endStr]) => {
      if (startStr === '00:00' && endStr === '23:59') return '24 Hours';
      let start = formatTimeString(startStr);
      const end = formatTimeString(endStr);
      if (start.slice(-2) === end.slice(-2)) start = start.slice(0, -2);
      return `${start} — ${end}`;
    });
    return formattedRanges.join(', ');
  });

  // Combine consecutive days with the same hours
  const hours = [];
  const daysArr = useFullDaysName ? FULL_DAY_INDEX : DAY_INDEX;
  let startDay = 0;
  let endDay = 0;
  for (let i = 1; i < 8; i += 1) {
    if (dayRanges[i] === dayRanges[i - 1]) endDay = i;
    else {
      hours.push(
        `${daysArr[startDay]}${
          startDay !== endDay
            ? `${useFullDaysName ? ' — ' : '—'}${daysArr[endDay]}`
            : ''
        }: ${dayRanges[startDay]}`,
      );
      startDay = i;
      endDay = i;
    }
  }
  return hours;
};
